import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { contentAPI, creatorPostsAPI } from '../services/api'
import type { Content as ContentType, PostsSummary, TopicIdea } from '../types'
import toast from 'react-hot-toast'
import { apiErrorMessage } from '../utils/apiError'

/**
 * Ideas to start from, tapped into the box. Lessons and process, not results:
 * a result belongs in a post from its receipt (Moments), where it's recorded.
 * Shown until today's ideas load (contentAPI.topicIdeas), and if they can't.
 */
export const EXAMPLE_TOPICS = [
  'Why I size every trade from the stop, not my conviction',
  'What funding rates tell you before a squeeze',
  'My rules for cutting a losing trade',
  'How I tell a real breakout from a fake one',
]

const FALLBACK_IDEAS: TopicIdea[] = EXAMPLE_TOPICS.map((topic) => ({ topic, kind: 'evergreen', theme: '', why: null }))

/**
 * The creator's best kind of post on a network, from real counts, once there
 * are enough posts to call it (PostsSummary.enough_for_pattern). Null otherwise.
 */
export const workingHint = (summary: PostsSummary | null): string | null => {
  for (const network of summary?.networks ?? []) {
    if (!network.enough_for_pattern || network.kinds.length < 2) continue
    const best = [...network.kinds].sort((a, b) => (b.avg_views ?? 0) - (a.avg_views ?? 0))[0]
    if (best.avg_views === null) continue
    return `On ${network.network_name}, your ${best.label.toLowerCase()} get the most views: ${best.avg_views.toLocaleString()} on average.`
  }
  return null
}

/**
 * One platform, one free-text topic, one post: the generator Library used to
 * hold. The post is saved as a draft; the link opens it in Library.
 */
const TopicGenerator = () => {
  const [generating, setGenerating] = useState(false)
  const [selectedType, setSelectedType] = useState<string>('twitter')
  const [topic, setTopic] = useState('')
  const [generated, setGenerated] = useState<ContentType | null>(null)
  const [hint, setHint] = useState<string | null>(null)
  const [ideas, setIdeas] = useState<TopicIdea[]>(FALLBACK_IDEAS)
  // How many times More ideas was pressed; reload refetches after a draft,
  // so the idea just used drops out.
  const [round, setRound] = useState(0)
  const [reload, setReload] = useState(0)
  // The server waits up to a couple of seconds on the market's prices.
  const [findingIdeas, setFindingIdeas] = useState(false)

  useEffect(() => {
    let stopped = false
    setFindingIdeas(true)
    Promise.resolve()
      .then(() => contentAPI.topicIdeas(round))
      .then((list) => !stopped && list?.length && setIdeas(list))
      .catch(() => undefined)
      .finally(() => !stopped && setFindingIdeas(false))
    return () => {
      stopped = true
    }
  }, [round, reload])

  useEffect(() => {
    let stopped = false
    // Optional: the hint shows only when there are enough real counts.
    Promise.resolve()
      .then(() => creatorPostsAPI.summary())
      .then((summary) => !stopped && setHint(workingHint(summary)))
      .catch(() => undefined)
    return () => {
      stopped = true
    }
  }, [])

  const handleGenerate = async () => {
    if (!topic.trim()) {
      toast.error('Please enter a topic')
      return
    }

    setGenerating(true)
    try {
      const content = await contentAPI.generate({
        content_type: selectedType,
        topic,
      })
      toast.success('Content generated successfully!')
      setGenerated(content)
      setTopic('')
      setReload((n) => n + 1)
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to generate content'))
    } finally {
      setGenerating(false)
    }
  }

  return (
    <div className="bg-white rounded-lg shadow px-4 py-3" data-testid="generate-row">
      <h2 className="text-base font-semibold text-gray-900">Generate new content</h2>
      <div id="generate-form" className="mt-3 flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[10rem]">
          <label htmlFor="generate-type" className="block text-sm font-medium text-gray-700 mb-2">
            Content Type
          </label>
          <select
            id="generate-type"
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="twitter">Twitter</option>
            <option value="linkedin">LinkedIn</option>
            <option value="instagram">Instagram</option>
            <option value="blog">Blog</option>
            <option value="youtube" disabled>YouTube (Coming Soon)</option>
            <option value="tiktok">TikTok</option>
          </select>
        </div>

        <div className="flex-1 min-w-[10rem]">
          <label htmlFor="generate-topic" className="block text-sm font-medium text-gray-700 mb-2">
            Topic
          </label>
          <input
            id="generate-topic"
            type="text"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="What do you want to say?"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <button
          onClick={handleGenerate}
          disabled={generating}
          className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
        >
          {generating ? 'Generating...' : 'Generate'}
        </button>
      </div>

      <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Example topics">
        {ideas.map((idea) => (
          <button
            key={idea.topic}
            type="button"
            onClick={() => setTopic(idea.topic)}
            title={idea.why ?? undefined}
            className={`rounded-full border px-3 py-1 text-xs hover:bg-gray-50 ${
              idea.kind === 'market' ? 'border-primary-300 text-primary-800' : 'border-gray-300 text-gray-700'
            }`}
          >
            {idea.topic}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setRound((n) => n + 1)}
          disabled={findingIdeas}
          className="rounded-full px-3 py-1 text-xs font-medium text-primary-700 hover:underline disabled:opacity-50"
        >
          {findingIdeas ? 'Finding ideas…' : 'More ideas'}
        </button>
      </div>
      {ideas.some((idea) => idea.kind === 'market') && (
        <p className="mt-2 text-xs text-gray-500" data-testid="market-ideas-note">
          {ideas
            .filter((idea) => idea.kind === 'market')
            .map((idea) => idea.why)
            .join('. ')}
          . The post quotes prices as the feed shows them when it's written.
        </p>
      )}
      {hint && (
        <p className="mt-2 text-xs text-gray-500" data-testid="working-hint">
          {hint}
        </p>
      )}

      {generated && (
        <div className="mt-3 flex items-center gap-3 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
          <span>Saved as a draft.</span>
          {/* Library opens the draft from the link's state, as Create's trade-idea flow does. */}
          <Link
            to="/content"
            state={{ openIds: [generated.id] }}
            className="ml-auto font-medium underline hover:text-green-900"
          >
            View in Library
          </Link>
        </div>
      )}
    </div>
  )
}

export default TopicGenerator
